import { Header } from '@cloistr/ui';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { CollectionEditorRoute } from '@/routes/CollectionEditorRoute';
import { EditorRoute } from '@/routes/EditorRoute';
import { Home } from '@/routes/Home';
import { MyPagesRoute } from '@/routes/MyPagesRoute';
import { ReadableRoute } from '@/routes/ReadableRoute';
import { SegmentRoute } from '@/routes/SegmentRoute';
import { SharedRoute } from '@/routes/SharedRoute';
import { ThemeEditorRoute } from '@/routes/ThemeEditorRoute';
import '@/styles/editor.css';
import '@/styles/page.css';

// CRITICAL: pass activeServiceId ONLY — never a `services` prop.
// The service list is owned by @cloistr/ui defaultServices.
//
// ThemeProvider (governance §5.6) MUST wrap the app root: it provides light/dark/
// system theming (default follows the OS) and the Header renders the theme toggle.
export default function App() {
  return (
    <BrowserRouter>
        <div className="cloistr-app">
          <Header activeServiceId="pages" />
          <main className="page-main">
            <Routes>
              <Route path="/" element={<Home />} />
              {/* The writing routes, and the ONLY ones that need a key. The
                  gate lives inside them rather than around the app, because a
                  page address in a URL has to render for a stranger with no
                  account -- that is the whole product. */}
              <Route path="/write" element={<EditorRoute />} />
              <Route path="/write/:naddr" element={<EditorRoute />} />
              {/* Themes are written here and worn by pages, so this is a
                  writing route too and carries the same gate. It sits above
                  the one-segment route below because `/theme` would otherwise
                  be read as a NIP-19 identifier and refused. */}
              <Route path="/theme" element={<ThemeEditorRoute />} />
              <Route path="/theme/:naddr" element={<ThemeEditorRoute />} />
              {/* A site is an ordered list of pages, written here and read at
                  its own address. Another writing route with the same gate, and
                  it sits above the one-segment route for the same reason
                  `/theme` does: `/site` would otherwise be read as a NIP-19
                  identifier and refused. */}
              <Route path="/site" element={<CollectionEditorRoute />} />
              <Route path="/site/:naddr" element={<CollectionEditorRoute />} />
              {/* The owner's own list. Gated like the editors, and above the
                  one-segment route because `/mine` would otherwise be read as
                  a NIP-19 identifier and refused. */}
              <Route path="/mine" element={<MyPagesRoute />} />
              {/* What other people shared with this reader. Gated, and above
                  the one-segment route for the same reason as `/mine`. */}
              <Route path="/shared" element={<SharedRoute />} />
              {/* One segment, and it is a NIP-19 identifier: an naddr for a
                  page, an npub or nprofile for an author's index. SegmentRoute
                  decodes it and dispatches. The readable two-segment form below
                  resolves INTO the page case. */}
              <Route path="/:id" element={<SegmentRoute />} />
              {/* The readable form: an author -- an `npub`, an `nprofile`, or a
                  name we serve -- and the page's own slug. It RESOLVES to the
                  canonical address above and renders it in place, never the
                  other way round, because a name service being down must not
                  take other people's links with it. */}
              <Route path="/:author/:slug" element={<ReadableRoute />} />
            </Routes>
          </main>
        </div>
    </BrowserRouter>
  );
}
